import React from 'react';

import { formatDate } from '@/lib/format-utils';

import { Properties } from '@/types/cit-public-api';

interface PropertiesViewProps {
  properties: Properties | undefined;
}

interface FormElementProps {
  label?: string | undefined;
  width?: string | undefined;
  content: React.ReactNode;
  caption?: boolean;
  nestLevel?: number;
}

const FormElement = ({ label, content, width, nestLevel = 0 }: FormElementProps) => {
  // widths are specified in mui's grid system, need to convert to tailwind widths
  const widths = width?.split(' ');
  const widthClasses =
    widths
      ?.map(
        (w) =>
          // target mobile screens and up first i.e w-xs-12 -> w-full and then override for larger screens and when there are multiple widths
          //  specified i.e w-md-6 -> lg:w-[50%]
          `${widths.length > 1 && w.split('-')[1] !== 'xs' ? 'lg:' : ''}w-${
            w.split('-')[2] === '12' ? 'full' : `${w.split('-')[2]}/12`
          }`
      )
      // default to full width if no widths are specified
      .join(' ') || 'w-full';

  return (
    <div className={`mb-4 pr-6 ${widthClasses}`}>
      {label ? (
        // Create header element based solely on nesting level, starting from h2 (skipping h1 for title)
        nestLevel < 5 ? (
          React.createElement(
            'h' + (nestLevel + 2), // h2 for nestLevel 0, h3 for nestLevel 1, h4 for nestLevel 2, etc.
            {
              className: `${nestLevel > 0 ? 'text-lg' : 'text-xl'} color-inherit`
            },
            label
          )
        ) : (
          <p className={`${nestLevel > 0 ? 'text-lg' : 'text-xl'} color-inherit`}>{label}</p>
        )
      ) : null}
      {content}
    </div>
  );
};

interface FormGroupProps {
  label?: string | undefined;
  children: React.ReactNode;
  nestLevel: number;
  isContainer?: boolean;
}

const FormGroup = ({ label, children, nestLevel, isContainer = false }: FormGroupProps) => (
  <div className='w-full color-inherit'>
    {label ? (
      // Create header element based solely on nesting level, starting from h2 (skipping h1 for title)
      nestLevel < 5 ? (
        React.createElement(
          'h' + (nestLevel + 2), // h2 for nestLevel 0, h3 for nestLevel 1, h4 for nestLevel 2, etc.
          {
            className: `${
              nestLevel > 0 ? `text-lg ${isContainer ? 'font-bold' : ''}` : 'text-xl font-bold'
            } color-inherit'
            }`
          },
          label
        )
      ) : (
        <p
          className={`${
            nestLevel > 0 ? `text-lg ${isContainer ? 'font-bold' : ''}` : 'text-xl font-bold'
          } color-inherit ${isContainer ? 'font-bold' : ''}`}
        >
          {label}
        </p>
      )
    ) : null}
    <div className='flex flex-wrap color-inherit'>{children}</div>
  </div>
);

type SimpleContentValue = React.ReactNode;

const SimpleContent = ({ value }: { value: SimpleContentValue | SimpleContentValue[] }) =>
  Array.isArray(value) ? (
    <React.Fragment>
      {value.map((v, idx) => (
        <span className='whitespace-pre-wrap inline-block text-sm' key={idx}>
          {v}
          {idx !== value.length - 1 ? ', ' : ''}
        </span>
      ))}
    </React.Fragment>
  ) : (
    <span className='whitespace-pre-wrap inline-block text-sm'>{value}</span>
  );

interface FuzzyDate {
  fuzzydate_value: string;
}

interface FuzzyDateRange {
  fuzzydate_from: FuzzyDate;
  fuzzydate_to: FuzzyDate;
}

const formatFuzzyDateRange = (fuzzyDate: FuzzyDateRange) => {
  const { fuzzydate_from, fuzzydate_to } = fuzzyDate;
  return `${(fuzzydate_from && fuzzydate_from.fuzzydate_value) || ''}${
    fuzzydate_from && fuzzydate_to ? ' - ' : ''
  }${(fuzzydate_to && fuzzydate_to.fuzzydate_value) || ''}`;
};

interface WebLink {
  weblink_value?: string;
  weblink_link: string;
}

const formatWebLink = (weblink: WebLink) => {
  const { weblink_value, weblink_link } = weblink;
  return (
    <a
      href={weblink_link}
      className='underline'
      style={{ color: 'inherit' }}
      target='_blank'
      rel='noopener noreferrer'
    >
      {weblink_value || weblink_link}
    </a>
  );
};

const formElementFactory = (
  property: Properties,
  valueType: string,
  label: string,
  key: string,
  nestLevel = 0
) => {
  const { width } = property;
  switch (valueType) {
    case 'value_text': {
      const { value_text } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={<SimpleContent value={value_text} />}
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_integer': {
      const { value_integer } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={<SimpleContent value={value_integer} />}
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_decimal': {
      const { value_decimal } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={<SimpleContent value={value_decimal.decimal_value} />}
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_date': {
      const { value_date } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_date)
                  ? value_date.map((d) => formatDate(d))
                  : formatDate(value_date)
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_length': {
      const { value_length } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_length)
                  ? value_length.map((l: { length_value: string }) => l.length_value)
                  : value_length.length_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_weight': {
      const { value_weight } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_weight)
                  ? value_weight.map((w: { weight_value: string }) => w.weight_value)
                  : value_weight.weight_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_reference': {
      const { value_reference } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_reference)
                  ? value_reference.map((r: { reference_value: string }) => r.reference_value)
                  : value_reference.reference_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_fuzzydate': {
      const { value_fuzzydate } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_fuzzydate)
                  ? value_fuzzydate.map((d: { fuzzydate_value: string }) => d.fuzzydate_value)
                  : value_fuzzydate.fuzzydate_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_fuzzydaterange': {
      const { value_fuzzydaterange } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_fuzzydaterange)
                  ? value_fuzzydaterange.map((d: FuzzyDateRange) => formatFuzzyDateRange(d))
                  : formatFuzzyDateRange(value_fuzzydaterange)
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_currency': {
      const { value_currency } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_currency)
                  ? value_currency.map((c: { currency_value: string }) => c.currency_value)
                  : value_currency.currency_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_duration': {
      const { value_duration } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_duration)
                  ? value_duration.map((c: { duration_value: string }) => c.duration_value)
                  : value_duration.duration_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_percent': {
      const { value_percent } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_percent)
                  ? value_percent.map((c: { percent_value: string }) => c.percent_value)
                  : value_percent.percent_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    case 'value_weblink': {
      const { value_weblink } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_weblink)
                  ? value_weblink.map((c: WebLink) => formatWebLink(c))
                  : formatWebLink(value_weblink)
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }
    default:
      console.log(`NO HANDLER FOR ${valueType}`);
      return null;
  }
};

const MAX_DEPTH = 20;

const propertiesViewMaker = (properties: Properties, keyPrefix: string, nestLevel: number) => {
  if (nestLevel > MAX_DEPTH) {
    return [];
  }

  const values = Object.values<Properties[keyof Properties]>(properties);
  const countValues = values.length;
  return values.reduce((acc, property, outerIndex) => {
    let { label } = property;
    // skip label if same as parent key and there's only one property
    if (keyPrefix.startsWith(label) && outerIndex === 0 && countValues === 1) {
      label = undefined;
    }
    // find value_* key
    const valueKey = Object.keys(property).find((k) => k.startsWith('value'));
    // if no valueKey found then just ignore property
    if (!valueKey) {
      return acc;
    }
    switch (valueKey) {
      case 'value_hreference_list': {
        const { value_hreference_list } = property;
        const children: React.ReactNode[] = [];
        if (Array.isArray(value_hreference_list)) {
          value_hreference_list.forEach((v, innerIndex) => {
            const fe = formElementFactory(
              v,
              'value_reference',
              v.label,
              `${label}-${innerIndex}`,
              nestLevel
            );
            if (fe) {
              children.push(fe);
            }
          });
        } else {
          const fe = formElementFactory(
            value_hreference_list,
            'value_reference',
            label
              ? label.startsWith(value_hreference_list.label)
                ? undefined
                : value_hreference_list.label
              : undefined,
            `${label}-${outerIndex}`,
            nestLevel
          );
          if (fe) {
            children.push(fe);
          }
        }
        acc.push(
          <FormGroup key={`${keyPrefix}-${outerIndex}`} label={label} nestLevel={nestLevel}>
            {children}
          </FormGroup>
        );
        break;
      }
      case 'value_reference': {
        const { value_reference } = property;
        // NOTE: Special handling for Profile projections.  Check if any Profile has projections
        // and if so tree each profile (there might be more than one) as a separate form element with
        // their own labeling
        const valueReferenceList = Array.isArray(value_reference)
          ? value_reference
          : [value_reference];
        if (valueReferenceList.some((v) => 'projections' in v)) {
          for (const [listIdx, valueRef] of valueReferenceList.entries()) {
            property.value_reference = valueRef;
            acc.push(
              formElementFactory(
                property,
                valueKey,
                label,
                `${keyPrefix}-${outerIndex}-${listIdx}`,
                nestLevel + 1
              )
            );
            if (valueRef.projections) {
              acc.push(
                ...propertiesViewMaker(
                  valueRef.projections,
                  `${label}-${outerIndex}-${listIdx}`,
                  nestLevel + 1
                )
              );
            }
          }
        } else {
          acc.push(
            formElementFactory(property, valueKey, label, `${keyPrefix}-${outerIndex}`, nestLevel)
          );
        }
        break;
      }
      case 'value': {
        const { value } = property;
        const children: React.ReactNode[] = [];
        if (Array.isArray(value)) {
          for (const [idx, v] of value.entries()) {
            children.push(...propertiesViewMaker(v, `${label}-${idx}`, nestLevel + 1));
          }
        } else {
          // peek at the sub-properties.  If there's only one and it's label is "similar" to ours
          // then suppress our label and use our only child's label
          const childKeys = Object.keys(value);
          if (childKeys.length === 1) {
            if (label.startsWith(value[childKeys[0]].label)) {
              label = '';
            }
          }
          children.push(...propertiesViewMaker(value, label, nestLevel + 1));
        }
        acc.push(
          <FormGroup
            key={`${keyPrefix}-${outerIndex}`}
            label={label}
            nestLevel={nestLevel}
            isContainer={true}
          >
            {children}
          </FormGroup>
        );
        break;
      }
      default:
        acc.push(
          formElementFactory(property, valueKey, label, `${keyPrefix}-${outerIndex}`, nestLevel)
        );
    }
    return acc;
  }, []);
};

export default function PropertiesView({ properties }: PropertiesViewProps) {
  return (
    <div className='mt-2 flex flex-wrap color-inherit'>
      {properties ? propertiesViewMaker(properties, 'root', 0) : null}
    </div>
  );
}
